/**
 * The child's own page.
 *
 * Reached at /me. A genuinely separate surface rather than the parent app with
 * things hidden -- it holds a different credential, talks to different routes
 * and cannot construct a request for anybody else's data.
 *
 * WHAT IS NOT HERE, and this is the screen where it matters most: no
 * analytics, no telemetry, no third-party script, no font from a CDN, no
 * error reporter, no request to anything except this app's own API. A child
 * uses this, so it is the page that has to be able to say that without
 * qualification. Nothing on it is recorded either: the picker, the waiting
 * state and the chosen person are local to this browser.
 *
 * The copy is addressed to a child, unlike everywhere else in the app. That is
 * a deliberate exception: "directed to children" is itself a COPPA trigger, so
 * every OTHER screen speaks to the adult holding the account. This one is the
 * child's, and reads like it.
 *
 * FOUR STATES, and the second one is new:
 *
 *   code     type eight characters, or arrive by link
 *   waiting  show a grown-up the match code, and wait for them to say yes
 *   picker   two children share this computer; whose valley is this?
 *   valley
 */

import { Loading } from '../components/Spinner';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ask,
  forgetOne,
  pollApproval,
  restoreAll,
  detectDeviceKind,
  type ChildSession,
  type PendingRequest,
} from './session';
import { clearChildCode, stashedChildCode } from './arrive';
import { whyRefused } from './session';
import { ChildApiError, childMove, loadOwn, proposeLook, readOwnRevision, type OwnGoal, type OwnState } from './state';
import { LivingValley } from '../valley/LivingValley';
import { isNight, sceneFor } from '../valley/scene';
import { assetUrl } from '../valley/sprites';
import { CountUp } from '../brand/CountUp';
import { GoalPicture } from '../brand/GoalPicture';
import { ProgressBar } from '../brand/ProgressBar';
import { appSide, nativeShell, rememberAppHome } from '../native/platform';
import { ValleyBackdrop } from '../brand/backdrop';
import { amountInputMode, amountPlaceholder, bigAmount, dollars, howMuch, looksLikeAmount, normaliseAmount } from '../family/money';
import { lockedAccounts } from '../auth/pin';
import { Builder } from '../avatar/Builder';
import { BuilderLaunch } from '../avatar/BuilderLaunch';
import { Character } from '../avatar/Character';
import { RememberedJar } from '../valley/MarbleJar';

/** Long enough not to hammer a throttled route, short enough to feel instant. */
const POLL_MS = 10_000;

export function ChildApp() {
  const [sessions, setSessions] = useState<ChildSession[] | null>(null);
  const [chosen, setChosen] = useState<ChildSession | null>(null);
  const [state, setState] = useState<OwnState | null>(null);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const [syncError, setSyncError] = useState('');
  const [night, setNight] = useState(() => isNight(new Date().getHours()));
  /** Designing a character. See `proposeLook`: it only ever asks. */
  const [styling, setStyling] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState('');
  /** Set when a parent has unlinked this device out from under a session. */
  const [unlinked, setUnlinked] = useState<string | null>(null);
  /** Linked, but a parent has paused it or not yet given permission. */
  const [held, setHeld] = useState<'paused' | 'consent' | null>(null);
  const chosenRef = useRef(chosen);
  chosenRef.current = chosen;

  useEffect(() => {
    setError('');
    setSyncError('');
  }, [chosen?.personId]);

  useEffect(() => {
    const update = () => setNight(isNight(new Date().getHours()));
    const timer = window.setInterval(update, 60_000);
    document.addEventListener('visibilitychange', update);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', update);
    };
  }, []);

  useEffect(() => {
    void restoreAll().then((found) => {
      setSessions(found);
      // One session is not a choice worth offering. Arriving by link always
      // means "add somebody", even when there is already a session here.
      if (found.length === 1 && !stashedChildCode()) setChosen(found[0]!);
      if (stashedChildCode()) setJoining(true);
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!chosen) return;
    const loaded = await loadOwn(chosen.token, chosen.key);
    // A sibling may have been selected while this request was in flight.
    if (chosenRef.current?.personId === chosen.personId &&
        chosenRef.current.token === chosen.token) setState(loaded);
  }, [chosen]);

  useEffect(() => {
    if (!chosen) return;
    void refresh().catch((problem: unknown) => {
      if (chosenRef.current?.personId !== chosen.personId ||
          chosenRef.current.token !== chosen.token) return;
      // A 403 means one thing here: a parent unlinked this device. Say so
      // and drop the session, rather than leaving a child tapping "Try
      // again" against a wall they can never get past. DO NOT retry.
      if (problem instanceof ChildApiError && problem.status === 403) {
        void whyRefused(chosen.token).then((why) => {
          if (why === 'gone') setUnlinked(chosen.personId);
          else setHeld(why);
        });
        return;
      }
      setError(problem instanceof Error ? problem.message : 'Something went wrong.');
    });
  }, [chosen, refresh]);

  // While held, look again now and then; the page comes back by itself when a
  // parent resumes it or gives permission.
  useEffect(() => {
    if (!held || !chosen) return;
    const timer = window.setInterval(() => {
      void refresh().then(() => setHeld(null)).catch(() => {});
    }, POLL_MS * 6);
    return () => window.clearInterval(timer);
  }, [held, chosen, refresh]);

  /** Keep an open page current, and notice when a parent unlinks its token. */
  useEffect(() => {
    if (!chosen || !state || unlinked) return;
    const { token, key, personId } = chosen;
    const revision = state.revision;
    let stopped = false;
    let inFlight = false;

    const poll = () => {
      if (stopped || inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      void readOwnRevision(token)
        .then(async (now) => {
          if (stopped) return;
          if (now !== revision) {
            const loaded = await loadOwn(token, key);
            if (!stopped) setState(loaded);
          }
          if (!stopped) setSyncError('');
        })
        .catch((problem: unknown) => {
          if (stopped) return;
          if (problem instanceof ChildApiError && problem.status === 403) {
            void whyRefused(token).then((why) => {
              if (why === 'gone') {
                setState(null);
                setUnlinked(personId);
              } else {
                setHeld(why);
              }
            });
          } else {
            setSyncError('Your valley could not update. It will try again.');
          }
        })
        .finally(() => { inFlight = false; });
    };

    const timer = window.setInterval(poll, POLL_MS);
    document.addEventListener('visibilitychange', poll);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [chosen, state?.revision, unlinked]);

  /**
   * A grown-up unlinked this device.
   *
   * Drop exactly this one session and go back, rather than retrying into a
   * wall. A sibling's session on the same computer is untouched, because a
   * parent unlinking the family PC from Susan's account has said nothing at
   * all about Ravi.
   */
  const dropped = useCallback(
    async (personId: string) => {
      await forgetOne(personId);
      const left = await restoreAll();
      setSessions(left);
      setChosen(null);
      setState(null);
    },
    [],
  );

  if (sessions === null) return <Shell><Loading fill label="Opening Birbly…" /></Shell>;

  if (held && !unlinked) {
    return (
      <Shell>
        <h2>{held === 'consent' ? 'Almost ready' : 'Taking a break'}</h2>
        <p>
          {held === 'consent'
            ? 'A grown-up needs to finish setting up Birbly before your page opens. Ask them to look in Birbly.'
            : 'A grown-up has paused your page for now. It will come back when they turn it on again.'}
        </p>
        <button className="primary full" onClick={() => void refresh().then(() => setHeld(null)).catch(() => {})}>
          Try again
        </button>
      </Shell>
    );
  }

  if (unlinked) {
    return (
      <Shell>
        <h2>A grown-up unlinked this</h2>
        <p>Ask them if you want to come back.</p>
        <button
          className="primary full"
          onClick={() => {
            const gone = unlinked;
            setUnlinked(null);
            void dropped(gone);
          }}
        >
          OK
        </button>
      </Shell>
    );
  }

  if (joining || (sessions.length === 0 && !chosen)) {
    return (
      <Shell>
        <Join
          canGoBack={sessions.length > 0}
          onBack={() => {
            clearChildCode();
            setJoining(false);
          }}
          onJoined={(session) => {
            clearChildCode();
            setJoining(false);
            setState(null);
            setSessions((current) => [
              ...(current ?? []).filter((one) => one.personId !== session.personId),
              session,
            ]);
            setChosen(session);
          }}
        />
      </Shell>
    );
  }

  if (!chosen) {
    return (
      <Shell>
        <Picker
          sessions={sessions}
          onChoose={setChosen}
          onAddSomebody={() => setJoining(true)}
        />
      </Shell>
    );
  }

  if (!state) {
    return (
      <Shell>
        {error ? (
          <>
            <p className="error">{error}</p>
            <div className="actions">
              <button
                className="primary"
                onClick={() => {
                  setError('');
                  void refresh().catch(() => setError('Still not working.'));
                }}
              >
                Try again
              </button>
              <button onClick={() => void dropped(chosen.personId)}>Start again</button>
            </div>
          </>
        ) : (
          <Loading fill label="Loading your valley…" />
        )}
      </Shell>
    );
  }

  const goals = state.goals.filter((goal) => goal.status === 'active');
  // Finished, and left up by a grown-up so the child can see they did it.
  const done = state.goals.filter((goal) => goal.status === 'completed');
  const free = state.balance - state.setAside;

  const openBuilder = () => {
    setSent('');
    setStyling(true);
  };

  // The display's builder, whole-screen. See avatar/Builder.tsx.
  if (styling) {
    return (
      <Builder
        initial={state.pendingAvatar ?? state.avatar}
        name={state.name}
        saveLabel={state.avatar ? 'Send updated look for approval' : 'Send for parent approval'}
        busy={sending}
        error={error}
        note="A parent reviews your character before it appears in the valley. You can update it later."
        onSave={(spec, name) => {
          setSending(true);
          setError('');
          void proposeLook(chosen.token, chosen.key, name, spec)
            .then(async () => {
              setStyling(false);
              setSent('Sent! A grown-up will have a look.');
              await refresh();
            })
            .catch((problem: unknown) =>
              setError(problem instanceof Error ? problem.message : 'That did not send.'),
            )
            .finally(() => setSending(false));
        }}
        onCancel={() => {
          setStyling(false);
          setError('');
        }}
      />
    );
  }

  const doneWith = () => {
    // "I'm done" forgets NOTHING. It goes back to the picker so the next
    // person can tap their own name, which is what a child handing a tablet
    // to their sibling actually means.
    setChosen(null);
    setState(null);
  };
  const postcard = postcardArt(state.balance, night);

  return (
    <Shell wide>
      <div className="kid">
        {/* THEIR VALLEY, as big as the screen allows, with them standing in
            it and their money over the sky. Living Valley D4. */}
        <section className="kid-scene" aria-label={state.name ? `${state.name}'s valley` : 'Your valley'}>
          <div className="child-valley kid-valley">
            <LivingValley
              balance={state.balance}
              night={night}
              people={state.avatar ? [{ id: chosen.personId, name: state.name, avatar: state.avatar }] : []}
            />
            {/* Top right, as on the full valley: the way to the builder. */}
            <BuilderLaunch
              spec={state.pendingAvatar ?? state.avatar}
              label={state.avatar ? 'Change my character' : 'Make my character'}
              onOpen={openBuilder}
            />
          </div>

          <div className="kid-top">
            <div className="child-family">
              {/* No "Switch" here any more: it did exactly what "I'm done"
                  beside it does (back to the picker), and the two of them
                  squeezed the family's name to a few letters on a phone. */}
              <p className="shell-family">{state.familyName || 'Your family'}</p>
            </div>
            <button type="button" className="kid-done" onClick={doneWith}>
              I&rsquo;m done
            </button>
          </div>

          <div className="kid-hero">
            <h1 className="kid-name">{state.name || 'This is yours'}</h1>
            <div className="kid-figure-row">
              <RememberedJar id={`child-${chosen.personId}`} hundredths={state.balance} size={84} />
              <p className="total-figure kid-balance">
                <CountUp id={`kid-${chosen.personId}`} value={state.balance} format={(n) => bigAmount(n).number} />
                {bigAmount(state.balance).word ? <span className="total-unit"> {bigAmount(state.balance).word}</span> : null}
              </p>
            </div>
            <p className="kid-line">
              <span className="visually-hidden">{state.name ? `${state.name}, this is yours. ` : 'This is yours. '}</span>
              {dollars(Math.max(0, free))} free
              {state.setAside > 0 ? <> &middot; {dollars(state.setAside)} in goals</> : null}
              {state.allowance && !state.allowance.paused ? (
                <>
                  {' '}&middot; +{dollars(state.allowance.amount)} on{' '}
                  {new Date(`${state.allowance.next}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long' })}
                </>
              ) : null}
            </p>
          </div>
        </section>

        <div className="kid-sheet">
          <span className="sheet-handle" aria-hidden="true" />
          {syncError ? <p className="error" role="status">{syncError}</p> : null}
          {sent ? <p className="child-note">{sent}</p> : null}
          {state.pendingAvatar && !sent ? (
            <p className="child-note">Your new character is waiting for a grown-up to say yes.</p>
          ) : null}

          {goals.length || done.length ? (
            <section className="kid-goals-block">
              <div className="section-head">
                <h2>My goals</h2>
                {done.length ? <span className="kid-done-count">{done.length} done</span> : null}
              </div>
              <div className="kid-goals">
                {done.map((goal) => (
                  <div className="goal goal-done postcard" key={goal.id}>
                    <span className="postcard-art" style={{ backgroundImage: postcard }} aria-hidden="true" />
                    <span className="petals" aria-hidden="true">
                      {Array.from({ length: 9 }, (_, i) => (
                        <i key={i} style={{ '--i': i } as React.CSSProperties} />
                      ))}
                    </span>
                    <div className="postcard-body">
                      <p className="postcard-eyebrow">You did it</p>
                      <p className="goal-title">{goal.title || 'A goal'}</p>
                      <p className="goal-money">Saved {dollars(goal.target)}</p>
                    </div>
                  </div>
                ))}
                {goals.map((goal) => (
                  <ChildGoal
                    key={goal.id}
                    goal={goal}
                    free={free}
                    art={postcard}
                    onMove={(amount, direction) =>
                      childMove(chosen.token, goal.id, amount, direction).then(() => refresh())
                    }
                  />
                ))}
              </div>
              {goals.length ? (
                <p className="footnote">
                  Free to spend: {dollars(free)}. The bar is everybody&rsquo;s money in the goal; &ldquo;You put in&rdquo; is yours.
                </p>
              ) : null}
            </section>
          ) : null}

          <section className="kid-history">
            <h2>What happened</h2>
            {state.entries.length === 0 ? (
              <p className="footnote">Nothing yet.</p>
            ) : (
              <ul className="entries">
                {state.entries.slice(0, 20).map((entry) => (
                  <li className={entry.voided ? 'entry voided' : 'entry'} key={entry.id}>
                    <div className="entry-body">
                      <p className="entry-reason">{entry.reason || 'A grown-up added this'}</p>
                      <p className="entry-date">{kidDate(entry.date, state.today)}</p>
                    </div>
                    <p className={entry.amount < 0 ? 'entry-amount out' : 'entry-amount in'}>
                      {entry.amount > 0 ? '+' : ''}
                      {dollars(entry.amount)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <p className="footnote kid-private">
            Only you can see this page. It does not show anybody else&rsquo;s money.
          </p>

          {sessions.length > 1 ? (
            <div className="actions">
              <button onClick={doneWith}>Not you?</button>
            </div>
          ) : null}

          <ForgetMe name={state.name} onForget={() => void dropped(chosen.personId)} />
        </div>
      </div>
    </Shell>
  );
}

/** The painting behind each goal postcard: their valley, by day or night. */
export function postcardArt(balance: number, night: boolean): string {
  const scene = sceneFor(balance, { hour: night ? 22 : 12, width: 1600, height: 900 });
  return `url("${assetUrl(scene.name, 'webp')}")`;
}

/** "Today", "Yesterday", or "Sat, Oct 10" -- the same words the parents see. */
export function kidDate(date: string, today: string): string {
  if (date === today) return 'Today';
  const d = new Date(`${today}T12:00:00`);
  d.setDate(d.getDate() - 1);
  if (date === d.toISOString().slice(0, 10)) return 'Yesterday';
  return new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

/**
 * One goal on a child's page: how much of their money is in it, and a way to
 * move their money in or back out.
 *
 * In: never more than they have free. Out: never more than they put in, and
 * not at all when a grown-up has locked the goal -- the button is replaced by
 * a lock that says so. Money taken out is free to spend again, or to put in
 * another goal. The server holds every one of these lines too.
 */
export function ChildGoal({
  goal,
  free,
  art,
  onMove,
}: {
  goal: OwnGoal;
  free: number;
  /** The CSS background for the postcard's painting. */
  art: string;
  /** Absent: a parent looking at the page -- the card shows, the buttons do not. */
  onMove?: (amount: string, direction: 'in' | 'out') => Promise<void>;
}) {
  const [mode, setMode] = useState<'in' | 'out' | null>(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  // Only to enable the button and say "too much"; the server decides.
  const typed = looksLikeAmount(amount) ? Math.round(parseFloat(normaliseAmount(amount)) * 100) : 0;
  const most = mode === 'out' ? goal.mine : free;
  const tooMuch = typed > most;
  const close = () => {
    setMode(null);
    setAmount('');
    setProblem('');
  };

  return (
    <div className={goal.pot >= goal.target && goal.target > 0 ? 'goal postcard is-ready' : 'goal postcard'}>
      <span className="postcard-art" style={{ backgroundImage: art }} aria-hidden="true" />
      <div className="goal-head">
        <GoalPicture icon={goal.icon} size={44} />
        <div className="goal-body">
          <p className="goal-title">
            {goal.title || 'A surprise'}
            {goal.locked ? <span className="visually-hidden"> (locked)</span> : null}
          </p>
          {!goal.title ? (
            <p className="footnote">
              Ask a grown-up to open this goal once and its name will show up here.
            </p>
          ) : null}
          {goal.pot !== goal.mine ? (
            <p className="goal-funds">You put in {dollars(goal.mine)}</p>
          ) : null}
        </div>
        {goal.locked ? <span className="status-pill locked" title="A grown-up locked this goal">Locked</span> : null}
      </div>
      <ProgressBar
        label={goal.title || 'Goal'}
        value={goal.pot}
        target={goal.target}
        full={goal.pot >= goal.target && goal.target > 0}
        now={dollars(goal.pot)}
        of={`of ${dollars(goal.target)}`}
      />
      {mode ? (
        <div className="put-in">
          <label className="field">
            <span>{mode === 'out' ? 'Take out how much?' : howMuch()}</span>
            <input
              value={amount}
              onChange={(event) => {
                setAmount(event.target.value);
                setProblem('');
              }}
              inputMode={amountInputMode()}
              placeholder={amountPlaceholder()}
              autoComplete="off"
              aria-label={mode === 'out' ? 'Amount to take out' : 'Amount to put in'}
            />
            <small>
              {mode === 'out'
                ? tooMuch
                  ? `You only put in ${dollars(goal.mine)}.`
                  : `You can take out up to ${dollars(goal.mine)}. It goes back to your money to spend.`
                : tooMuch
                  ? `You only have ${dollars(free)} to put in.`
                  : `You have ${dollars(free)} you can put in.`}
            </small>
          </label>
          {problem ? <p className="error">{problem}</p> : null}
          <div className="actions">
            <button aria-busy={busy}
              type="button"
              className="primary"
              disabled={busy || !typed || tooMuch}
              onClick={() => {
                setBusy(true);
                void onMove!(normaliseAmount(amount), mode)
                  .then(close)
                  .catch((error: unknown) => setProblem(error instanceof Error ? error.message : 'That did not work.'))
                  .finally(() => setBusy(false));
              }}
            >
              {busy ? 'Moving it…' : mode === 'out' ? 'Take it out' : 'Put it in'}
            </button>
            <button type="button" disabled={busy} onClick={close}>
              Not now
            </button>
          </div>
        </div>
      ) : !onMove ? null : (
        <span className="secret-actions kid-goal-actions">
          {free > 0 ? (
            <button type="button" className="primary" onClick={() => setMode('in')}>
              Put some in
            </button>
          ) : null}
          {goal.mine > 0 && !goal.locked ? (
            <button type="button" onClick={() => setMode('out')}>
              Take some out
            </button>
          ) : null}
          {goal.mine > 0 && goal.locked ? (
            <span className="footnote">Locked by a grown-up: money can go in, not out.</span>
          ) : null}
        </span>
      )}
    </div>
  );
}


/**
 * Whose valley is this?
 *
 * Only when more than one person uses this device. Each name is opened with
 * that person's OWN key, so this list is assembled from several separate
 * sessions and nothing reads across them.
 */
function Picker({
  sessions,
  onChoose,
  onAddSomebody,
}: {
  sessions: ChildSession[];
  onChoose: (session: ChildSession) => void;
  onAddSomebody: () => void;
}) {
  const [names, setNames] = useState<Record<string, { name: string; family: string; avatar?: OwnState['avatar'] }>>({});

  useEffect(() => {
    let cancelled = false;
    void Promise.all(
      sessions.map(async (session) => {
        try {
          const own = await loadOwn(session.token, session.key);
          return [session.personId, { name: own.name, family: own.familyName, avatar: own.avatar }] as const;
        } catch {
          // A session a parent has unlinked. Still listed, so tapping it
          // gives the child the "a grown-up unlinked this" message rather
          // than making their name vanish with no explanation.
          return [session.personId, { name: '', family: '', avatar: null }] as const;
        }
      }),
    ).then((pairs) => {
      if (!cancelled) setNames(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [sessions]);

  return (
    <>
      <h2>Who is this?</h2>
      {sessions.map((session) => (
        <button
          key={session.personId}
          className="full picker-choice"
          onClick={() => onChoose(session)}
        >
          <span className="face picker-face" aria-hidden="true">
            {names[session.personId]?.avatar ? <Character spec={names[session.personId]!.avatar!} size={56} /> : null}
          </span>
          <span className="picker-name">{names[session.personId]?.name || 'Tap here'}</span>
          {names[session.personId]?.family ? (
            <span className="picker-family">{names[session.personId]!.family}</span>
          ) : null}
        </button>
      ))}
      <button className="text-button" onClick={onAddSomebody}>
        Somebody else, or another family
      </button>
      <GrownUpWayBack />
    </>
  );
}

/**
 * The way back to the grown-ups' side, from a child's device.
 *
 * Shown only when a parent has actually left an account on this browser --
 * on a child's own tablet there is nothing to go back to and this would be a
 * door to a sign-in page they have no business at.
 *
 * IT GRANTS NOTHING. It is a link to `/`, where a PIN or a Cognito password
 * is waiting. A child who taps it learns only what they could learn by typing
 * the address, which they can already do. What it buys is the other half of
 * the account picker: a family machine where the child's page is the one that
 * opens by default, and a parent should not have to know to edit the URL.
 */
function GrownUpWayBack() {
  // Read once on mount rather than watched. The parent surface cannot add a
  // locked account while this screen is on top of it -- it is the same tab.
  const [parents] = useState(() => lockedAccounts().length);
  if (parents === 0) return null;

  return (
    <p className="footnote">
      <button className="text-button" onClick={() => window.location.assign('/')}>
        A grown-up? Go to your account &rarr;
      </button>
    </p>
  );
}

/**
 * Typing a code, and then waiting for a grown-up.
 *
 * The waiting is the part that is new, and it is the whole security model on
 * screen: a code gets this device as far as "asking", and a person has to
 * look at it and agree. The match code is what they check -- if the screen in
 * the parent's hand shows a different animal from the screen in the child's,
 * the request in the list is not the device in the room.
 */
function Join({
  canGoBack,
  onBack,
  onJoined,
}: {
  canGoBack: boolean;
  onBack: () => void;
  onJoined: (session: ChildSession) => void;
}) {
  const [code, setCode] = useState(() => stashedChildCode() ?? '');
  const [request, setRequest] = useState<PendingRequest | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Poll while waiting. Stops the moment the answer arrives, or the screen
  // goes away, so a tablet left on this page is not asking forever.
  useEffect(() => {
    if (!request) return;
    let cancelled = false;

    const timer = window.setInterval(() => {
      void pollApproval(request).then((outcome) => {
        if (cancelled) return;
        if (outcome.status === 'approved') {
          onJoined(outcome.session);
          return;
        }
        if (outcome.status === 'gone') {
          setRequest(null);
          setError('A grown-up said no. Ask them if you think that is wrong.');
        }
        if (outcome.status === 'expired') {
          setRequest(null);
          setError('This took too long. Type your code again.');
        }
      });
    }, POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [request, onJoined]);

  if (request) {
    return (
      <>
        <h2>Show this to a grown-up</h2>
        <p className="match-code">{request.match}</p>
        <p>Waiting for them to say yes.</p>
        <p className="footnote">
          They need to open Birbly and tap <strong>Approve</strong>. Make sure
          the picture they see is the same as the one up there.
        </p>
        <button
          className="text-button"
          onClick={() => {
            setRequest(null);
            setError('');
          }}
        >
          Start again
        </button>
      </>
    );
  }

  // THE DEVICE PICKER USED TO BE HERE: four buttons asking a child whether
  // this was a phone, a tablet, a computer or something else. It is worked
  // out now -- `detectDeviceKind` -- so there is one less screen between a
  // child and their valley.
  //
  // The reason for asking was never that the answer was unknowable. It was
  // that a parent reads the label when deciding which request to approve. A
  // guess is good enough for that: the label is a hint, and the MATCH CODE on
  // the screen below is what actually tells two requests apart, because it
  // comes from the device's own public key.

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        setError('');
        setBusy(true);
        void ask(code, detectDeviceKind())
          .then(setRequest)
          .catch((problem: unknown) => {
            setError(
              problem instanceof Error ? problem.message : 'That did not work.',
            );
          })
          .finally(() => setBusy(false));
      }}
    >
      <h2>Your valley</h2>
      <p>Ask a grown-up for your code and type it here.</p>
      <p className="footnote">
        In more than one family &mdash; two homes, or grandparents? Type each
        family&rsquo;s code once and you can switch between them.
      </p>

      <label className="field">
        <span>Your code</span>
        <input
          value={code}
          onChange={(event) => setCode(event.target.value)}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={12}
          placeholder="K7QM-3XR9"
        />
        <small>Eight letters and numbers. Big or small, it does not matter.</small>
      </label>

      {error ? <p className="error">{error}</p> : null}

      <button aria-busy={busy} className="primary full" type="submit" disabled={busy || !code.trim()}>
        {busy ? 'Asking…' : 'Ask a grown-up'}
      </button>

      {canGoBack ? (
        <button type="button" className="text-button" onClick={onBack}>
          Back
        </button>
      ) : null}

      <p className="footnote">
        A grown-up has to say yes before this page is yours. Your code keeps
        working, so you can use it on another computer too.
      </p>
      <p className="footnote">
        <strong>Lost your code?</strong> A grown-up can find it in Birbly: tap
        <strong> Family</strong>, then <strong>Code &amp; link</strong> by your
        name. They can show you a square to point this camera at, too.
      </p>
    </form>
  );
}

/** Behind a second tap, because it is the one thing here that loses something. */
function ForgetMe({ name, onForget }: { name: string; onForget: () => void }) {
  const [sure, setSure] = useState(false);

  if (!sure) {
    return (
      <button className="text-button" onClick={() => setSure(true)}>
        Forget me on this device
      </button>
    );
  }
  return (
    <div className="danger-box">
      <p>
        This computer will forget {name || 'you'}. Your money and your valley
        are safe &mdash; they are not on here.
      </p>
      <p>To come back you will need your code and a grown-up to say yes.</p>
      <div className="actions">
        <button className="danger-button" onClick={onForget}>
          Forget me
        </button>
        <button onClick={() => setSure(false)}>Stay</button>
      </div>
    </div>
  );
}

function Shell({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) {
  // Wide: the child's own page, which draws its own valley. Otherwise the
  // picker and joining screens: a frosted card over a friendly valley.
  return (
    <main className={wide ? 'shell child child-own' : 'shell child shell-plain'}>
      {wide ? null : <ValleyBackdrop />}
      {wide ? children : <section className="panel">{children}</section>}
      <footer className="legal-links">
        <a href="/privacy">Privacy</a> · <a href="/terms">Terms</a>
        {/* In the app, the way across to the parents' side. The web has its
            own address for that; the app opens wherever it was last. */}
        {/* Birbly Kids has no grown-ups' side: that is the other app. */}
        {nativeShell() && appSide() !== 'kids' ? <> · <a href="/" onClick={() => rememberAppHome('/')}>Grown-ups</a></> : null}
      </footer>
    </main>
  );
}
